import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ActionKey, type AccountingSessionContext } from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';

export default function AccountingDayPanel() {
  const token = useAuthStore((s) => s.accessToken);
  const actor = useAuthStore((s) => s.user?.id);
  const revision = useAuthStore((s) => s.sessionRevision);
  return <AccountingDayPanelBody key={`${actor}:${sessionVenueKey(token)}:${revision}`} />;
}
function AccountingDayPanelBody() {
  const { t } = useTranslation('accounting');
  const qc = useQueryClient();
  const actor = useAuthStore((s) => s.user?.id);
  const token = useAuthStore((s) => s.accessToken);
  const revision = useAuthStore((s) => s.sessionRevision);
  const actions = useAuthStore((s) => s.allowedActions);
  const scope = [actor, sessionVenueKey(token), revision];
  const [confirmed, setConfirmed] = useState(false);
  const [operationKey, setOperationKey] = useState(() => crypto.randomUUID());
  const context = useQuery<AccountingSessionContext>({
    queryKey: ['accounting-session-context', ...scope],
    queryFn: () => api.get('/accounting/session-context'),
    enabled: !!actor && !!token,
    refetchOnWindowFocus: 'always',
  });
  useEffect(() => {
    setConfirmed(false);
    setOperationKey(crypto.randomUUID());
  }, [context.data?.currentBusinessDate]);
  const mutation = useMutation({
    mutationFn: () =>
      api.post('/accounting/business-day/cutover', {
        expectedBusinessDate: context.data!.currentBusinessDate,
        operationKey,
      }),
    onSuccess: async () => {
      setConfirmed(false);
      setOperationKey(crypto.randomUUID());
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['accounting-session-context'] }),
        qc.invalidateQueries({ queryKey: ['accounting-active-session'] }),
        qc.invalidateQueries({ queryKey: ['accounting-business-day'] }),
      ]);
    },
  });
  if (context.isError) return <p role="alert">{t('businessDayLoadFailed')}</p>;
  if (!context.data) return <p role="status">{t('cashSummaryLoading')}</p>;
  return (
    <section
      className="mb-5 rounded-xl border border-base-300 p-4 space-y-3"
      aria-label={t('businessDayContext')}
    >
      <p>{t('currentBusinessDay', { date: context.data.currentBusinessDate })}</p>
      {context.data.pendingSessions.length > 0 && (
        <div>
          <p>{t('pendingAccounts')}</p>
          <ul>
            {context.data.pendingSessions.map((s) => (
              <li key={s.id}>
                <Link
                  className="link min-h-11 inline-flex items-center"
                  to={`/accounting/settle?sessionId=${s.id}`}
                >
                  {s.sessionDate.slice(0, 10)} — {t('pendingClose')}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {actions?.includes(ActionKey.ACCOUNTING_CLOSE) && (
        <div className="space-y-2">
          <label className="flex min-h-11 gap-2 items-center">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={mutation.isPending}
            />
            {t('earlyCutoverConfirmation')}
          </label>
          <button
            className="btn btn-outline min-h-11"
            disabled={!confirmed || mutation.isPending || context.isFetching}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t('switchingDay') : t('switchBusinessDay')}
          </button>
        </div>
      )}
      {mutation.isError && <p role="alert">{t('businessDaySwitchFailed')}</p>}
    </section>
  );
}
